import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { HeartFilled, HeartOutlined } from "@ant-design/icons";
import { Image, Typography } from "antd";
import { addWishList, getWishListApi } from "../Features/WishList/WishList";
import defualtImage from "../Assets/PNG/image.png";
import styles from "../Pages/HomePage/index.module.scss";
import { BASE_URL } from "../Constant";
import { getUsdToGbp, getCachedRate } from "../utils/fxRate";
import { applyRetailerPricing } from "../utils/vatHelper";
import { slugify } from "../utils/categoryUrl";

const { Text, Title } = Typography;

const getFromPrice = (item) => {
  if (item?.minPrice != null && Number(item.minPrice) > 0)
    return Number(item.minPrice);
  if (item?.startingPrice != null && Number(item.startingPrice) > 0)
    return Number(item.startingPrice);
  if (item?.price != null && Number(item.price) > 0)
    return Number(item.price);

  const prices = [];
  (item?.components || []).forEach((component) => {
    // Skip switched-off qualities. Without this the tile can advertise a "from"
    // built on a quality the product page will not sell — and a deactivated
    // quality is usually the CHEAPEST one, so it is exactly the price Math.min
    // would pick.
    (component?.qualityVariants || []).filter((q) => q?.active !== false).forEach((q) => {
      const p = Number(
        q?.finalPrice ??
          (Number(q?.mainDiamondPrice || 0) + Number(q?.otherTypesPrice || 0)),
      );
      if (p > 0) prices.push(p);
    });
  });

  return prices.length > 0 ? Math.min(...prices) : null;
};

export const ProductCard = ({ item }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [fxRate, setFxRate] = useState(getCachedRate);

  useEffect(() => { if (!fxRate) getUsdToGbp().then(r => { if (r) setFxRate(r); }); }, [fxRate]);

  const userToken = useSelector((state) => state.user?.token);
  const givewishlist = useSelector((state) => state.wishList?.wishlist);

  const isProductLiked = Boolean(
    givewishlist?.find((e) => e._id === item?._id),
  );

  const toggleLike = (e) => {
    e.stopPropagation();
    const obj = { productId: item?._id };
    const onSuccessCallback = () => {
      dispatch(getWishListApi(userToken));
    };
    dispatch(addWishList(obj, onSuccessCallback, userToken));
  };

  const mouseDownPos = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);

  const handleMouseDown = (e) => {
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    const dx = Math.abs(e.clientX - mouseDownPos.current.x);
    const dy = Math.abs(e.clientY - mouseDownPos.current.y);
    if (dx > 5 || dy > 5) isDragging.current = true;
  };

  const handleNavigate = () => {
    if (isDragging.current) return;
    navigate(`/product-detail/${slugify(item?.Product_Name)}`, {
      state: { productId: item?._id },
    });
  };

  const fromPriceRaw = getFromPrice(item);
  // minPrice from API is already in GBP — no FX conversion needed
  const fromPrice = fromPriceRaw != null ? applyRetailerPricing(fromPriceRaw) : null;

  const formattedPrice =
    fromPrice != null
      ? `£${fromPrice.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : null;

  return (
    <div
      className={styles.productCard}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onClick={handleNavigate}
    >
      {/* Image Container */}
      <div className={styles.imageWrapper}>
        {(() => {
          const imgs = item?.Product_Images || [];
          const src = imgs.length
            ? `${BASE_URL}/${imgs[0]?.path}`
            : defualtImage;
          // Second image (if the product has one) crossfades in on hover.
          const secondSrc =
            imgs.length > 1 && imgs[1]?.path
              ? `${BASE_URL}/${imgs[1].path}`
              : null;
          // key={src} forces the Image (and its inner <img>) to fully
          // unmount/remount when the URL changes. Without this, a reused
          // <img> DOM node keeps painting the previously-loaded pixels
          // while the browser fetches the new src — visible as a flash
          // of the previous product's image with the new product's text.
          return (
            <>
              <Image
                key={src}
                preview={false}
                src={src}
                alt={item?.Product_Name}
                className={styles.productImage}
              />
              {/* Hover preview — a white-backed overlay so the contained
                  second image fully covers the first; opacity is driven by
                  .productCard:hover in CSS (pure enhancement, no-op on touch). */}
              {secondSrc && (
                <img
                  src={secondSrc}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className={styles.productImageHover}
                />
              )}
            </>
          );
        })()}

        {/* Wishlist Overlay */}
        {userToken && (
          <div className={styles.wishlistBtn} onClick={toggleLike}>
            {isProductLiked ? (
              <HeartFilled className={styles.heartFilled} />
            ) : (
              <HeartOutlined className={styles.heartOutlined} />
            )}
          </div>
        )}

        {/* Kids badge. Top-LEFT on purpose: the wishlist heart owns the top
            right and the Shipping label the bottom left, so this is the only
            corner where it can never sit on top of something else. */}
        {item?.Kids && <span className={styles.kidsBadge}>Kids</span>}

        {/* Badge for Shipping */}
        {item?.Shipping && (
          <span className={styles.shippingBadge}>{item?.Shipping}</span>
        )}
      </div>

      {/* Content Section */}
      <div className={styles.details}>
        {/* Four different endpoints feed this card and they do not agree on the
            brand's shape — some send a string, some the populated { Data_Name }
            Data doc. Rendering that object threw "Objects are not valid as a
            React child" and, with no error boundary in this app, blanked the
            whole page. Never trust the shape here. */}
        <Text className={styles.brandName}>
          {item?.brand ||
            (typeof item?.Brand_Name === "string" ? item.Brand_Name : item?.Brand_Name?.Data_Name) ||
            item?.Brand?.Brand_Name ||
            ""}
        </Text>

        <Title level={5} className={styles.productTitle}>
          {item?.Product_Name}
        </Title>

        <div className={styles.priceRow}>
          {(() => {
            try { const s = JSON.parse(localStorage.getItem("tdc_retailer_settings") || "{}"); if (s.Show_Prices === false) return <span className={styles.priceValue}>£POA</span>; } catch {}
            return <><span className={styles.fromLabel}>from</span><span className={styles.priceValue}>{formattedPrice ?? "£POA"}</span></>;
          })()}
        </div>
      </div>
    </div>
  );
};
